// @ts-nocheck
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ColorType,
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  LineStyle,
} from "lightweight-charts";
import {
  GetAccount200ResponsePositionsInner,
  GetOrders200Response,
} from "../../codegen-api";
import { TEXT_COLORS } from "../../constants/design/colors";
import { BORDER_RADIUS, COMPONENTS } from "../../constants/design/spacing";
import { tripletToHex } from "../../utils/directionColors";
import { getAssetShortName } from "../../utils/asset/assets";
import { SettingsContext } from "../../contexts/SettingsContext";
import { Container } from "./style";

// ── Global-CEX (Binance) candle source ──────────────────────────────────────
// A low-volume/testnet venue's own trades are too sparse to chart, so we show the
// global underlying price from Binance (the GMX/oracle model). Rendered with the
// self-hosted lightweight-charts so candles are fully COLORABLE. Used as the
// automatic FALLBACK when the TradingView embed script is blocked/offline (and
// for any symbol TradingView can't resolve). Mirrors apps/perps — but this repo
// is on lightweight-charts v4, so it uses `addCandlestickSeries` (not v5's addSeries).
const BINANCE_REST = "https://api.binance.com/api/v3/klines";
const BINANCE_WS = "wss://stream.binance.com:9443/ws";
const LS_INTERVAL = "PERPS_CHART_INTERVAL";

const INTERVALS: { label: string; value: string }[] = [
  { label: "1m", value: "1m" },
  { label: "5m", value: "5m" },
  { label: "15m", value: "15m" },
  { label: "1h", value: "1h" },
  { label: "4h", value: "4h" },
  { label: "1D", value: "1d" },
];

function toBinanceSymbol(symbol: string): string {
  const short = (getAssetShortName(symbol) || symbol || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  const base = short.replace(/USDT?$|PERP$|USD$/g, "") || short;
  return `${base}USDT`;
}

// Candle colors resolved from the live --color-up/--color-down vars. NOTE:
// lightweight-charts **v4** (this repo) can't parse the CSS Color-4 space syntax
// `rgb(22 199 132)` that cssColor returns (v5 can) — so we convert the triplet to
// HEX via tripletToHex, which v4 parses fine.
function readTriplet(name: string, fallback: string): string {
  const raw =
    typeof window !== "undefined"
      ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      : "";
  return raw || fallback;
}

function seriesColors() {
  const up = tripletToHex(readTriplet("--color-up", "22 199 132"));
  const down = tripletToHex(readTriplet("--color-down", "234 57 67"));
  return {
    upColor: up,
    downColor: down,
    borderUpColor: up,
    borderDownColor: down,
    wickUpColor: up,
    wickDownColor: down,
    borderVisible: false,
  };
}

interface ITradingViewChartProps {
  symbol: string;
  orders?: GetOrders200Response[];
  position?: GetAccount200ResponsePositionsInner;
  onCancelOrder?: (order: GetOrders200Response) => void;
  load?: boolean;
  isMobileScreen?: boolean;
  amountDecimals?: number;
  priceDecimals?: number;
  hidden?: boolean;
}

function LightweightCandles({
  symbol,
  isMobileScreen,
  hidden,
  load = true,
  priceDecimals = 2,
}: ITradingViewChartProps) {
  const { directionScheme, customDirection } = useContext(SettingsContext);
  const binanceSymbol = useMemo(() => toBinanceSymbol(symbol), [symbol]);

  const [interval, setIntervalValue] = useState<string>(
    () => localStorage.getItem(LS_INTERVAL) || "15m"
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);

  // Build the chart + candle series, load Binance history, and stream realtime.
  useEffect(() => {
    if (!containerRef.current || !load) return undefined;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#0e0e14" },
        textColor: "#9598a1", // TradingView-style muted axis text
        fontSize: 12,
        fontFamily: "'Trebuchet MS', Roboto, Ubuntu, sans-serif",
        // Remove the lightweight-charts TradingView attribution logo (bottom-left)
        // that links out to tradingview.com.
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: "rgba(255, 255, 255, 0.05)" },
        horzLines: { color: "rgba(255, 255, 255, 0.05)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 4,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#363a45",
        },
        horzLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#363a45",
        },
      },
      // v4 has a built-in watermark option (v5 needs createTextWatermark) — the
      // faint centered symbol gives the signature TradingView look.
      watermark: {
        visible: true,
        text: (getAssetShortName(symbol) || symbol || "").toUpperCase(),
        color: "rgba(255, 255, 255, 0.05)",
        fontSize: 46,
        fontStyle: "bold",
        horzAlign: "center",
        vertAlign: "center",
      },
    });
    // v4 API: addCandlestickSeries (v5 uses addSeries(CandlestickSeries, ...))
    const series = chart.addCandlestickSeries({
      ...seriesColors(),
      priceFormat: {
        type: "price",
        precision: priceDecimals,
        minMove: 1 / Math.pow(10, priceDecimals),
      },
    });
    chartRef.current = chart;
    seriesRef.current = series;

    let ws: WebSocket | null = null;
    let cancelled = false;

    fetch(
      `${BINANCE_REST}?symbol=${binanceSymbol}&interval=${interval}&limit=1000`
    )
      .then((r) => r.json())
      .then((raw: any[]) => {
        if (cancelled || !Array.isArray(raw)) return;
        series.setData(
          raw.map((k) => ({
            time: Math.floor(k[0] / 1000),
            open: parseFloat(k[1]),
            high: parseFloat(k[2]),
            low: parseFloat(k[3]),
            close: parseFloat(k[4]),
          }))
        );
        chart.timeScale().fitContent();
      })
      .catch(() => {});

    ws = new WebSocket(
      `${BINANCE_WS}/${binanceSymbol.toLowerCase()}@kline_${interval}`
    );
    ws.onmessage = (evt) => {
      try {
        const k = JSON.parse(evt.data).k;
        if (!k || cancelled || !seriesRef.current) return;
        seriesRef.current.update({
          time: Math.floor(k.t / 1000),
          open: parseFloat(k.o),
          high: parseFloat(k.h),
          low: parseFloat(k.l),
          close: parseFloat(k.c),
        });
      } catch {
        /* ignore malformed frames */
      }
    };

    return () => {
      cancelled = true;
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      try {
        chart.remove();
      } catch {
        /* ignore */
      }
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [binanceSymbol, interval, load, priceDecimals]);

  // Live re-theme candles when the direction scheme (or custom colors) change.
  useEffect(() => {
    if (seriesRef.current) seriesRef.current.applyOptions(seriesColors());
  }, [directionScheme, customDirection]);

  const onSelectInterval = (value: string) => {
    setIntervalValue(value);
    try {
      localStorage.setItem(LS_INTERVAL, value);
    } catch {
      /* ignore */
    }
  };

  return (
    <Container
      style={{
        height: isMobileScreen ? `${COMPONENTS.mobileTvChart}px` : "100%",
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{ display: "flex", gap: 4, padding: "6px 8px", flex: "0 0 auto" }}
      >
        {INTERVALS.map((it) => (
          <button
            key={it.value}
            type="button"
            onClick={() => onSelectInterval(it.value)}
            style={{
              padding: "2px 8px",
              fontSize: 12,
              cursor: "pointer",
              borderRadius: BORDER_RADIUS.one,
              border: "none",
              background:
                interval === it.value
                  ? "rgb(var(--color-primary, 76 215 244) / 0.16)"
                  : "transparent",
              color:
                interval === it.value ? TEXT_COLORS.one : TEXT_COLORS.three,
            }}
          >
            {it.label}
          </button>
        ))}
      </div>
      <div ref={containerRef} style={{ flex: "1 1 auto", minHeight: 0 }} />
    </Container>
  );
}

export type { ITradingViewChartProps };
export default LightweightCandles;
